import React from 'react';
import { MdCheck } from 'react-icons/md';
import classNames from 'classnames';
import { useSelect } from 'downshift';

import { FieldLabel } from '../field';
import { Icon } from '../icon';
import { ListboxCommonProps } from './listbox.model';
import { defaultItemToString, nonNullItemToString } from './listbox.util';
import { ListboxButton } from './listbox-button.component';
import { ListboxOption } from './listbox-option.component';
import { ListboxOptions } from './listbox-options.component';

export type ListboxProps<T> = ListboxCommonProps<T> & {
  value?: T | null;
  onChange?: (selectedItem: T) => void;
};

export function Listbox<T>({
  label,
  items,
  value,
  disabled,
  hasError,
  required,
  showRequiredIndicator = true,
  fullWidth = true,
  placeholder = 'Select',
  truncate = true,
  errorId,
  variant,
  height,
  className,
  itemToString = defaultItemToString,
  onChange,
  onMenuBlur,
}: ListboxProps<T>) {
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect<T>({
    items,
    itemToString: nonNullItemToString(itemToString),
    selectedItem: value,
    onStateChange: (changes) => {
      if (changes.type === useSelect.stateChangeTypes.MenuBlur && onMenuBlur) {
        onMenuBlur();
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem && onChange) {
        onChange(selectedItem);
      }
    },
  });

  const buttonText = selectedItem ? itemToString(selectedItem) : placeholder;

  return (
    <>
      <FieldLabel
        {...getLabelProps()}
        hasError={hasError}
        showRequiredIndicator={required && showRequiredIndicator}
      >
        {label}
      </FieldLabel>

      <div
        className={classNames('relative inline-block', {
          'w-full': fullWidth,
        })}
      >
        <ListboxButton
          disabled={disabled}
          hasError={hasError}
          fullWidth={fullWidth}
          truncate={truncate}
          variant={variant}
          height={height}
          isPlaceholder={!selectedItem}
          className={className}
          {...getToggleButtonProps({
            'aria-required': required,
            'aria-describedby': errorId,
          })}
        >
          {buttonText}
        </ListboxButton>
        <ListboxOptions
          isOpen={isOpen}
          fullWidth={fullWidth}
          {...getMenuProps()}
        >
          {isOpen &&
            items.map((item, index) => (
              <ListboxOption
                key={`${item}${index}`}
                highlighted={highlightedIndex === index}
                {...getItemProps({
                  item,
                  index,
                })}
              >
                <Icon
                  size="16"
                  icon={
                    <MdCheck
                      className={classNames(
                        'flex-shrink-0 stroke-2 text-teal-500 mr-3',
                        {
                          invisible: item !== selectedItem,
                        }
                      )}
                    />
                  }
                />
                <div
                  className={classNames({
                    truncate,
                    'font-medium': item === selectedItem,
                  })}
                >
                  {itemToString(item)}
                </div>
              </ListboxOption>
            ))}
        </ListboxOptions>
      </div>
    </>
  );
}
